import React, { useCallback, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { MagnifyingGlass, Warning, ArrowDown } from "@phosphor-icons/react";

/**
 * OrphanProductsPanel — collapsible diagnostic for Masters.
 *
 * Finds products stored in team members' isolated workspaces (team member
 * logged in with Google before being invited → auto-bootstrap created a
 * separate workspace). Lets the Master reclaim them in one click.
 *
 * Only renders when the current user is a Master; `onReload` is called after
 * a successful reclaim so the Products grid refreshes.
 */
export default function OrphanProductsPanel({ onReload }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [findings, setFindings] = useState(null); // null = never ran; [] = clean
  const [reclaiming, setReclaiming] = useState(null); // source_user_id in progress

  const run = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/orphan-products");
      setFindings(data.findings || []);
      if (!data.findings || data.findings.length === 0) {
        toast.success("Nenhum produto órfão encontrado — seu workspace está limpo.");
      } else {
        const total = data.findings.reduce((s, f) => s + f.products_count, 0);
        toast.warning(`Encontrados ${total} produto(s) em workspace(s) separado(s).`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao verificar produtos órfãos");
    } finally {
      setLoading(false);
    }
  }, []);

  const reclaim = async (source_user_id) => {
    if (!window.confirm("Trazer todos os produtos desse membro para o seu workspace?\n\nIsto remapeia os produtos para você e tenta re-vincular ao Contratante de mesmo nome. Produtos com código duplicado ao seu catálogo são pulados.")) return;
    setReclaiming(source_user_id);
    try {
      const { data } = await api.post("/admin/reclaim-products", { source_user_id });
      let msg = `${data.moved} produto(s) migrado(s)`;
      if (data.remapped_contractor) msg += ` · ${data.remapped_contractor} re-vinculado(s) ao Contratante`;
      if (data.code_conflicts?.length) msg += ` · ${data.code_conflicts.length} pulado(s) por código duplicado`;
      toast.success(msg);
      await run(); // refresh findings
      if (onReload) onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao migrar produtos");
    } finally {
      setReclaiming(null);
    }
  };

  return (
    <div data-testid="orphan-products-panel" className="card-brutal bg-brand-cream p-4 space-y-3">
      <button
        type="button"
        data-testid="orphan-toggle"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-3 text-left"
      >
        <div className="flex items-center gap-2">
          <Warning weight="bold" size={20} className="text-brand-pink" />
          <span className="font-heading text-lg font-black">Produtos cadastrados por outro membro?</span>
        </div>
        <ArrowDown weight="bold" size={18} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="space-y-3 pt-1 border-t-2 border-brand-ink">
          <p className="text-sm text-brand-ink2">
            Se um colaborador fez login com Google antes de ser convidado, os produtos dele podem ter ficado em um workspace separado.
            Clique abaixo pra procurar e trazer pro seu workspace.
          </p>
          <button
            type="button"
            data-testid="orphan-scan-btn"
            onClick={run}
            disabled={loading}
            className="btn-pink h-10 disabled:opacity-40"
          >
            <MagnifyingGlass weight="bold" size={16} />
            {loading ? "Verificando…" : "Verificar produtos órfãos"}
          </button>

          {findings !== null && findings.length === 0 && (
            <p className="text-sm text-brand-success font-bold" data-testid="orphan-clean">
              ✓ Nenhum produto órfão. Seu workspace está limpo.
            </p>
          )}

          {findings && findings.length > 0 && (
            <div className="space-y-3" data-testid="orphan-findings">
              {findings.map((f) => (
                <div key={f.source_user_id} className="border-2 border-brand-ink bg-white p-3 rounded">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div>
                      <p className="font-bold">{f.source_name || "(sem nome)"} <span className="text-brand-ink2 font-normal">· {f.source_email}</span></p>
                      <p className="text-sm">
                        <strong>{f.products_count}</strong> produto(s) tagueados a um Contratante seu, mas em workspace separado.
                      </p>
                    </div>
                    <button
                      data-testid={`orphan-reclaim-${f.source_user_id}`}
                      onClick={() => reclaim(f.source_user_id)}
                      disabled={reclaiming === f.source_user_id}
                      className="btn-brutal bg-brand-peach text-brand-ink hover:bg-brand-pink hover:text-white h-10 disabled:opacity-40"
                    >
                      {reclaiming === f.source_user_id ? "Migrando…" : "Trazer pro meu workspace"}
                    </button>
                  </div>
                  {f.products?.length > 0 && (
                    <details className="mt-2">
                      <summary className="cursor-pointer text-sm font-bold text-brand-ink">
                        Ver amostra ({Math.min(f.products.length, 10)} de {f.products_count})
                      </summary>
                      <ul className="mt-2 space-y-1 text-sm">
                        {f.products.slice(0, 10).map((p) => (
                          <li key={p.id} className="flex gap-2">
                            <span className="font-mono text-brand-ink2">{p.code}</span>
                            <span className="flex-1 truncate">{p.description}</span>
                            <span className="text-brand-pink font-bold">{p.contractor_name}</span>
                          </li>
                        ))}
                      </ul>
                    </details>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
